import { appToast } from "@components/Toast";
import { handleAPIGeocodeGetAddress } from "@components/address/API/handleAddressAPI";
import type { AddAddressFormData } from "@components/address/AddAddressFlowSheet";
import MapPicker from "@components/address/MapPicker";
import { cleanKeyword } from "@components/address/func/addressTextTool";
import { addressToGeocode, combinedAddress, geocodeToAddress, getCurrentLocation } from "@components/address/func/addressTool";
import { isAddressDeliverable, type DeliveryArea } from "@components/address/func/isAddressDeliverable";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Input, Spinner, Stack, Text, XStack, YStack } from "tamagui";
import KioskIcon from "../KioskIcon";
import { kiosk } from "../kioskTheme";
import { KioskCTA } from "./KioskParts";

type AddrForm = {
  city: string;
  area: string;
  road: string;
  streetNumber: string;
  floor: string;
  room: string;
  latitude: number | null;
  longitude: number | null;
};

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  flex,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  flex?: number;
  required?: boolean;
}) => (
  <YStack flex={flex} gap={6}>
    <Text fontSize={12} color={kiosk.grey} fontWeight="600">
      {label}
      {required && <Text color={kiosk.danger}> *</Text>}
    </Text>
    <Input
      value={value}
      onChangeText={(t: string) => onChange(t)}
      placeholder={placeholder}
      borderWidth={1.5}
      borderColor={kiosk.line}
      borderRadius={11}
      backgroundColor="#fff"
      color={kiosk.ink}
      fontSize={15}
      h={46}
    />
  </YStack>
);

/**
 * Kiosk 原生新增地址（保留地圖）：
 * 縣市/區/路/門牌/樓/室欄位 + 重用 MapPicker（Leaflet）微調座標。
 * 座標來源：「使用目前位置」GPS、「在地圖上定位」geocode、或直接拖曳地圖。
 * 送出沿用 AddAddressFormData 形狀，交給上層 createCustomerAddress。
 */
const KioskAddressSheet = ({
  isOpen,
  onClose,
  onSubmit,
  deliveryAreas,
  autoLocate,
  initial,
  title,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddAddressFormData) => void | Promise<void>;
  /**
   * 此瓦斯行的配送範圍。傳入時：選完縣市/區後即時檢查是否可配送，
   * 不可送則顯示警告文字並禁用儲存按鈕。未傳 → 零行為差異。
   */
  deliveryAreas?: DeliveryArea[];
  /** 開啟時自動觸發一次 GPS 定位並帶入地址（給「允許定位」入口用）。 */
  autoLocate?: boolean;
  /** 預填欄位（例：定位帶入的地址，給「確認送達地址」用）。 */
  initial?: Partial<AddrForm>;
  /** 自訂 header 標題（預設「新增送達地址」）。 */
  title?: string;
}) => {
  const methods = useForm<AddrForm>({
    defaultValues: { city: "", area: "", road: "", streetNumber: "", floor: "", room: "", latitude: null, longitude: null, ...initial },
  });
  const { setValue, watch, getValues } = methods;
  const { lineAccessToken } = useLineAuth();
  const v = watch();
  // 配送範圍提示：僅在有傳 deliveryAreas 且已選 city+area 且該地址不可配送時為 true。
  /** MapPicker 定位中 → 卡控儲存，避免存到尚未更新的座標 */
  const [isMapGeocoding, setIsMapGeocoding] = useState(false);
  const outOfDeliveryRange = deliveryAreas !== undefined && !!v.city && !!v.area && !isAddressDeliverable(deliveryAreas, v.city, v.area);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k: keyof AddrForm) => (val: string) => setValue(k, val as any);

  // 由座標反查地址並帶入欄位（失敗不擋，至少有座標）
  // 有 LINE token → 用正規化 API（台灣切分較準）；否則（含 dev）→ 用免授權的 Nominatim。
  const fillFieldsFromLatLng = async (lat: number, lng: number) => {
    if (lineAccessToken) {
      const geo = await handleAPIGeocodeGetAddress({ lat, lng }, lineAccessToken);
      const a = geo?.possibleList?.[0] as Record<string, any> | undefined;
      if (a) {
        if (a.administrative_area_level_1) setValue("city", a.administrative_area_level_1);
        if (a.administrative_area_level_2) setValue("area", a.administrative_area_level_2);
        if (a.route) setValue("road", a.route);
        if (a.street_number) setValue("streetNumber", cleanKeyword(a.street_number, "號"));
        return true;
      }
    }
    const r = await geocodeToAddress(lat, lng);
    if (!r) return false;
    if (r.city) setValue("city", r.city);
    if (r.area) setValue("area", r.area);
    if (r.road) setValue("road", r.road);
    if (r.streetNumber) setValue("streetNumber", cleanKeyword(r.streetNumber, "號"));
    return true;
  };

  /** 只列出真正缺的欄位（反向地理編碼常查不到門牌號，通用四欄訊息會讓人看不出缺哪個）。 */
  const missingFields = (d: { city?: string; area?: string; road?: string; streetNumber?: string }) =>
    [!d.city && "縣市", !d.area && "區", !d.road && "路名", !d.streetNumber && "門牌號"].filter(Boolean) as string[];

  const useGps = async () => {
    setLocating(true);
    const res = await getCurrentLocation();
    if (res.state !== "success") {
      setLocating(false);
      appToast.error(res.errorMsg || "定位失敗");
      return;
    }
    const lat = Number(res.lat);
    const lng = Number(res.lng);
    setValue("latitude", lat);
    setValue("longitude", lng);
    const filled = await fillFieldsFromLatLng(lat, lng);
    setLocating(false);
    if (!filled) {
      appToast.success("已定位目前位置，可在地圖微調");
      return;
    }
    // 台灣反向地理編碼多半帶不出門牌號 → 明說要補，避免「看起來已帶入」卻在送出時被擋。
    const miss = missingFields(getValues());
    appToast.success(miss.length ? `已帶入地址，請再補上${miss.join("、")}` : "已帶入目前位置地址，可再微調");
  };

  // 「允許定位」入口：開啟時自動跑一次 GPS 帶入地址（每次開啟只跑一次）。
  const autoRan = useRef(false);
  useEffect(() => {
    if (!isOpen) { autoRan.current = false; return; }
    if (autoLocate && !autoRan.current) {
      autoRan.current = true;
      useGps();
    }
  }, [isOpen, autoLocate]);

  // 漸進式地理編碼：Nominatim 對含門牌號的台灣地址常查不到，逐步放寬
  // （完整 → 去門牌 → 路名層級），讓圖釘至少落在路上，再由使用者拖曳微調。
  const geocodeProgressive = async (city: string, area: string, road: string, streetNumber: string) => {
    const queries = [
      combinedAddress({ city, area, road, streetNumber }),
      combinedAddress({ city, area, road }),
      `${city}${area}${road}`,
    ];
    const tried = new Set<string>();
    for (const q of queries) {
      if (!q || tried.has(q)) continue;
      tried.add(q);
      const res = await addressToGeocode(q);
      if (res.state === "success") return res;
    }
    return { state: "error" as const, lat: "", lng: "" };
  };

  const locateByAddress = async () => {
    const { city, area, road, streetNumber } = getValues();
    const missLoc = missingFields({ city, area, road, streetNumber });
    if (missLoc.length) {
      appToast.error(`請先填寫${missLoc.join("、")}`);
      return;
    }
    setLocating(true);
    const res = await geocodeProgressive(city, area, road, streetNumber);
    setLocating(false);
    if (res.state === "success") {
      setValue("latitude", Number(res.lat));
      setValue("longitude", Number(res.lng));
      appToast.success("已在地圖定位，請拖曳圖釘微調到正確門口");
    } else {
      appToast.error("找不到此地址座標，請改用「目前位置」或拖曳地圖");
    }
  };

  const handleSave = async () => {
    const d = getValues();
    const miss = missingFields(d);
    if (miss.length) {
      appToast.error(`請填寫${miss.join("、")}`);
      return;
    }
    // 配送範圍守門（與畫面禁用按鈕雙重保障）：所選地址不在此瓦斯行配送範圍 → 擋送出
    if (deliveryAreas !== undefined && !isAddressDeliverable(deliveryAreas, d.city, d.area)) {
      appToast.error(`此瓦斯行無法配送到${d.city}${d.area}`);
      return;
    }
    setSaving(true);
    try {
      let lat = d.latitude;
      let lng = d.longitude;
      if (lat == null || lng == null) {
        const res = await geocodeProgressive(d.city, d.area, d.road, d.streetNumber);
        if (res.state === "success") {
          lat = Number(res.lat);
          lng = Number(res.lng);
        }
      }
      await onSubmit({
        city: d.city,
        area: d.area,
        road: d.road,
        streetNumber: d.streetNumber,
        floor: d.floor || undefined,
        room: d.room || undefined,
        otherAddress: undefined,
        latitude: lat,
        longitude: lng,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <YStack
      className="kiosk-sheetin"
      position="absolute"
      t={0}
      l={0}
      r={0}
      b={0}
      backgroundColor="#fff"
      style={{ zIndex: 120 }}>
      {/* header */}
      <XStack px={18} py={14} ai="center" gap={12} style={{ flex: "0 0 auto" }}>
        <Stack onPress={onClose} cursor="pointer" width={26} pressStyle={{ opacity: 0.5 }}>
          <KioskIcon name="close" size={22} color={kiosk.grey} />
        </Stack>
        <Text flex={1} fontSize={17} fontWeight="800" color={kiosk.ink}>
          {title || "新增送達地址"}
        </Text>
      </XStack>

      <YStack flex={1} px={18} pb={16} className="kiosk-noscroll" style={{ overflowY: "auto" }} gap={12}>
        <XStack gap={10}>
          <Field label="縣市" value={v.city} onChange={set("city")} placeholder="台中市" flex={1} required />
          <Field label="區" value={v.area} onChange={set("area")} placeholder="西屯區" flex={1} required />
        </XStack>
        {outOfDeliveryRange ? (
          <Text fontSize={13} color={kiosk.danger} fontWeight="600">
            此瓦斯行無法配送到{v.city}
            {v.area}
          </Text>
        ) : null}
        <Field label="路名 / 街道" value={v.road} onChange={set("road")} placeholder="逢甲路" required />
        <XStack gap={10}>
          <Field label="門牌號" value={v.streetNumber} onChange={set("streetNumber")} placeholder="20號" flex={1.2} required />
          <Field label="樓層" value={v.floor} onChange={set("floor")} placeholder="3" flex={1} />
          <Field label="室（選填）" value={v.room} onChange={set("room")} placeholder="" flex={1} />
        </XStack>

        {/* 定位按鈕 */}
        <XStack gap={10} mt={2}>
          <XStack
            flex={1}
            ai="center"
            jc="center"
            gap={7}
            py={11}
            borderRadius={12}
            backgroundColor={kiosk.pillBg}
            cursor="pointer"
            onPress={useGps}
            pressStyle={{ opacity: 0.8 }}>
            {locating ? <Spinner size="small" color={kiosk.blue} /> : <KioskIcon name="pin" size={16} color={kiosk.blue} />}
            <Text fontSize={13} fontWeight="700" color={kiosk.blue}>
              使用目前位置
            </Text>
          </XStack>
          <XStack
            flex={1}
            ai="center"
            jc="center"
            gap={7}
            py={11}
            borderRadius={12}
            backgroundColor={kiosk.pillBg}
            cursor="pointer"
            onPress={locateByAddress}
            pressStyle={{ opacity: 0.8 }}>
            {locating ? <Spinner size="small" color={kiosk.blue} /> : <KioskIcon name="pin" size={16} color={kiosk.blue} />}
            <Text fontSize={13} fontWeight="700" color={kiosk.blue}>
              在地圖上定位
            </Text>
          </XStack>
        </XStack>

        {/* 地圖（拖曳微調座標） */}
        <YStack borderRadius={14} overflow="hidden" borderWidth={1} borderColor={kiosk.line} height={240}>
          <MapPicker formFields={v as any} setValue={setValue as any} moveHintText="拖曳地圖來微調定位" onGeocodingChange={setIsMapGeocoding} />
        </YStack>
        <Text fontSize={11} color={kiosk.grey}>
          可拖曳地圖上的圖釘微調實際門口位置。
        </Text>
      </YStack>

      <YStack px={18} pt={12} pb={24} style={{ flex: "0 0 auto" }}>
        <KioskCTA
          label={isMapGeocoding ? "定位中…" : saving ? "儲存中…" : "儲存地址"}
          disabled={saving || outOfDeliveryRange || isMapGeocoding}
          onPress={handleSave}
          right={saving ? <Spinner size="small" color="#fff" /> : undefined}
        />
      </YStack>
    </YStack>
  );
};

export default KioskAddressSheet;
